import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getEnvVar } from "ideas.env";
import { ToolStatus, ToolVisibility } from "types/constants";
import { ToolSpec } from "types/json-schemas/type_guards";
import { getRequestHeaders } from "utils/getRequestHeaders";
import { Tool as _Tool } from "graphql/_Types";

export type Tool = {
  id: _Tool["id"];
  key: string;
  name: string;
  date_created: string;
  help_text: string | null;
  status: ToolStatus;
  pinned: boolean;
  visibility: ToolVisibility;
  docs_link: string | null;
  code_link: string | null;
  tags: string[];
  tenant: {
    id: number;
    key: string;
    name: string;
  } | null;
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  latest_tool_version: {
    id: number;
    version: string;
    status: ToolStatus;
    date_created: string;
    credits: number | null;
    tool_spec: ToolSpec | null;
  } | null;
};

const ALL_TOOLS_QUERY_KEY = "all_tools";

export const useAllToolsQuery = () => {
  return useQuery<Tool[], Error>({
    queryKey: [ALL_TOOLS_QUERY_KEY],
    queryFn: async () => {
      const baseUrl = getEnvVar("URL_TRS");
      const url = `${baseUrl}tool/browse/`;
      const headers = await getRequestHeaders();
      const res = await axios.get<Tool[]>(url, { headers });
      return res.data;
    },
  });
};

export const usePinToolMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (toolId: Tool["id"]) => {
      const baseUrl = getEnvVar("URL_TRS");
      const url = `${baseUrl}tool/${toolId}/pin/`;
      const headers = await getRequestHeaders();
      await axios.patch(url, undefined, { headers });
      await client.invalidateQueries([ALL_TOOLS_QUERY_KEY]);
    },
  });
};

export const useUnpinToolMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (toolId: Tool["id"]) => {
      const baseUrl = getEnvVar("URL_TRS");
      const url = `${baseUrl}tool/${toolId}/unpin/`;
      const headers = await getRequestHeaders();
      await axios.patch(url, undefined, { headers });
      await client.invalidateQueries([ALL_TOOLS_QUERY_KEY]);
    },
  });
};

export const useUpdateToolMutation = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      id: Tool["id"];
      name: string;
      help_text: string;
    }) => {
      const { id, ...body } = data;
      const baseUrl = getEnvVar("URL_TRS");
      const url = `${baseUrl}tool/${id}/`;
      const headers = await getRequestHeaders();
      await axios.patch(url, body, { headers });
      await client.invalidateQueries([ALL_TOOLS_QUERY_KEY]);
    },
  });
};
